import { useState } from 'react';
import { Box, Card, Flex, Heading, IconButton, Menu, Portal, Tag, Text } from '@chakra-ui/react';
import { LuEllipsisVertical } from 'react-icons/lu';
import CreatedBy from '@components/CreatedBy';

import type { CampaignType } from '../types';
import ConfirmDisableDialog from './ConfirmDisableDialog';
import ConfirmStartCampaignDialog from './ConfirmStartCampaignDialog';
import { useAuthContext } from '@contexts/auth';

type Props = {
  campaign: CampaignType,
  refetch: () => void,
};

const CampaignCard = ({ campaign, refetch }: Props) => {
  const { checkIfHasRole } = useAuthContext();

  const [showConfirmDisableDialog, setShowConfirmDisableDialog] = useState(false);
  const [showConfirmStartCampaignDialog, setShowConfirmStartCampaignDialog] = useState(false);

  const isEnabled = campaign.is_enabled;

  const canStartServices = checkIfHasRole('CAN_START_CAMPAIGN_EXECUTION');
  const canDisableCampaigns = checkIfHasRole('CAN_DISABLE_CAMPAIGNS');

  const showMenu = canStartServices || canDisableCampaigns;

  return (
    <Card.Root
      size="sm"
      variant="elevated"
      bg="grayscale.fg"
      mt={4}
      px={4}
      py={3}
    >
      <Flex justifyContent="space-between" alignItems="center" gap={2}>
        <Box>
          <Flex
            gap={{ base: 1, md: 2 }}
            flexDirection={{ base: 'column', lg: 'row' }}
            mb={{ base: 1, md: 0 }}
          >
            <Heading
              size="md"
              textDecoration={isEnabled ? 'none' : 'line-through'}
              color={isEnabled ? 'fg.default' : 'fg.error'}
            >
              {campaign.name}
            </Heading>
            <Flex gap={1} flexWrap="wrap">
              {campaign.tags.map((tag) => (
                <Tag.Root key={tag} size="md" bg="argos.fg" color="grayscale" fontWeight={600}>
                  <Tag.Label>{tag}</Tag.Label>
                </Tag.Root>
              ))}
            </Flex>
          </Flex>
          <Text color="fg.muted">
            {campaign.portfolio.name}
          </Text>
          <CreatedBy user={campaign.user} createdDate={campaign.created_date} />
        </Box>
        {isEnabled && showMenu && (
          <Menu.Root>
            <Menu.Trigger asChild>
              <IconButton variant="ghost" size="sm">
                <LuEllipsisVertical />
              </IconButton>
            </Menu.Trigger>
            <Portal>
              <Menu.Positioner>
                <Menu.Content>
                  {canStartServices && (
                    <Menu.Item
                      value="start"
                      color="fg.success"
                      _hover={{
                        bg: "bg.success",
                        color: "fg.success"
                      }}
                      onClick={() => setShowConfirmStartCampaignDialog(true)}
                    >
                      Iniciar atendimento
                    </Menu.Item>
                  )}
                  {canDisableCampaigns && (
                    <Menu.Item
                      value="disable"
                      color="fg.error"
                      _hover={{
                        bg: "bg.error",
                        color: "fg.error"
                      }}
                      onClick={() => setShowConfirmDisableDialog(true)}
                    >
                      Desabilitar
                    </Menu.Item>
                  )}
                </Menu.Content>
              </Menu.Positioner>
            </Portal>
          </Menu.Root>
        )}
      </Flex>
      {showConfirmDisableDialog && (
        <ConfirmDisableDialog
          campaign={campaign}
          handleClose={() => setShowConfirmDisableDialog(false)}
          afterSubmit={refetch}
        />
      )}
      {showConfirmStartCampaignDialog && (
        <ConfirmStartCampaignDialog
          campaign={campaign}
          handleClose={() => setShowConfirmStartCampaignDialog(false)}
        />
      )}
    </Card.Root>
  );
};

export default CampaignCard;
